import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import axios from "axios";
import QRCode from "react-qr-code";
import { Copy, Check, TrendingUp, Users, DollarSign, ExternalLink, CreditCard, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AffiliateDashboardPage = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    fetchAffiliate();
    
    // Check if returning from Stripe
    const stripeStatus = searchParams.get("stripe");
    if (stripeStatus === "complete") {
      toast.success("Stripe account connected successfully!");
    } else if (stripeStatus === "refresh") {
      toast.info("Please complete your Stripe setup");
    }
  }, [code, searchParams]);

  const fetchAffiliate = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/dashboard?code=${code}`);
      setAffiliate(response.data);
    } catch (error) {
      console.error("Failed to fetch affiliate:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}?ref=${affiliate.affiliate_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const connectStripe = async () => {
    setStripeLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/stripe-onboard/${code}`);
      if (response.data.already_complete) {
        toast.success("Your Stripe account is already connected!");
        fetchAffiliate();
      } else if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Stripe setup");
    } finally {
      setStripeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-['Playfair_Display'] text-white mb-4">Affiliate not found</h2>
          <Link to="/">
            <Button className="btn-gold">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const trackingUrl = `${window.location.origin}?ref=${affiliate.affiliate_code}`;

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-2xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
          <span className="text-white/50 text-sm">Affiliate Dashboard</span>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-[#D4AF37] text-sm tracking-widest uppercase">Affiliate Dashboard</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-white mt-2">
            Welcome, <span className="text-[#D4AF37]">{affiliate.name}</span>
          </h1>
          <p className="text-white/50 mt-2">Track your referrals and commissions</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="affiliate-stats">
          <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Total Sales</p>
                <p className="text-3xl font-bold text-white" data-testid="total-sales">{affiliate.total_sales}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-white" data-testid="total-revenue">€{affiliate.total_revenue.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#047857]/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#047857]" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Commission Earned</p>
                <p className="text-3xl font-bold text-[#047857]" data-testid="total-commission">€{affiliate.total_commission.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tracking Tools */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* QR Code */}
          <div className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10 text-center">
            <h3 className="font-['Playfair_Display'] text-xl text-white mb-6">Your QR Code</h3>
            <div className="bg-white p-6 rounded-xl inline-block" data-testid="affiliate-qr">
              <QRCode 
                value={trackingUrl}
                size={180}
                fgColor="#020617"
              />
            </div>
            <p className="text-white/50 text-sm mt-6">
              Print this QR code or share it digitally. When scanned, it will track referrals to your account.
            </p>
          </div>

          {/* Tracking Link */}
          <div className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
            <h3 className="font-['Playfair_Display'] text-xl text-white mb-6">Your Tracking Link</h3>
            
            <div className="mb-6">
              <p className="text-white/50 text-sm mb-2">Affiliate Code</p>
              <p className="font-mono text-[#D4AF37] text-xl" data-testid="affiliate-code">{affiliate.affiliate_code}</p>
            </div>

            <div className="mb-6">
              <p className="text-white/50 text-sm mb-2">Your Unique Link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={trackingUrl}
                  readOnly
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-mono"
                  data-testid="tracking-url"
                />
                <Button 
                  onClick={copyLink}
                  className="btn-gold px-4 py-3 rounded"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/50 text-sm">
                Share this link on social media, your website, or with friends. You earn <span className="text-[#D4AF37] font-bold">15% commission</span> on every membership purchased through your link.
              </p>
            </div>

            <a 
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#D4AF37] mt-6 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Preview your landing page</span>
            </a>
          </div>
        </div>

        {/* Commission Info */}
        <div className="mt-8 bg-gradient-to-r from-[#D4AF37]/10 to-[#047857]/10 rounded-2xl p-8 border border-[#D4AF37]/20">
          <h3 className="font-['Playfair_Display'] text-xl text-white mb-4">How Commissions Work</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="text-[#D4AF37] font-bold text-2xl">15%</p>
              <p className="text-white/50 text-sm">Commission Rate</p>
            </div>
            <div>
              <p className="text-white font-bold text-2xl">Instant</p>
              <p className="text-white/50 text-sm">Attribution Tracking</p>
            </div>
            <div>
              <p className="text-white font-bold text-2xl">Automatic</p>
              <p className="text-white/50 text-sm">Stripe Payouts</p>
            </div>
          </div>
        </div>

        {/* Stripe Connect Section */}
        <div className="mt-8 bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${affiliate.stripe_onboarding_complete ? 'bg-[#047857]/20' : 'bg-[#D4AF37]/10'}`}>
              {affiliate.stripe_onboarding_complete ? (
                <CheckCircle className="w-6 h-6 text-[#047857]" />
              ) : (
                <CreditCard className="w-6 h-6 text-[#D4AF37]" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">
                {affiliate.stripe_onboarding_complete ? 'Stripe Connected' : 'Connect Stripe to Get Paid'}
              </h3>
              {affiliate.stripe_onboarding_complete ? (
                <p className="text-white/50 text-sm mb-4">
                  Your Stripe account is connected. Commissions will be automatically transferred to your account.
                </p>
              ) : (
                <>
                  <p className="text-white/50 text-sm mb-4">
                    Connect your Stripe account to receive automatic commission payouts when customers purchase through your link.
                  </p>
                  <Button 
                    onClick={connectStripe}
                    disabled={stripeLoading}
                    className="btn-gold px-6 py-3 rounded"
                    data-testid="connect-stripe-btn"
                  >
                    {stripeLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Connect Stripe Account
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {!affiliate.stripe_onboarding_complete && (
            <div className="mt-6 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-500/80 text-sm">
                  You must connect Stripe to receive commission payouts. Without it, your earnings will be tracked but not paid out.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;
